import React, { useState, useEffect } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import {
    FormControl,
    FormLabel,
    TextField,
    Switch,
    FormControlLabel,
    Box,
    Button,
    IconButton,
    Chip,
    Select,
    MenuItem
} from "@mui/material";
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    ViewList as ViewListIcon,
    GridView as GridViewIcon
} from "@mui/icons-material";
import { StackContainer } from '../../../Stack/StackContainer';
import { getSelectionStyle } from '../../../Stack/utils/stackUtils';
import ViewSwitcher from '../../../Stack/views/ViewSwitcher';

export const StackCraft = ({
    name = "StackCraft",
    rootId = null,
    debug = false,
    rows = 100,
    clickable = true,
    showCapture = true,
    sideBySide = false,
    listWidth = "350px",
    width = "350px",
    height = "400px",
    detailIds = [],
    showViewSelector = true,
    showFuzzyToggle = true,
    // Grid props
    viewMode = "tree",
    columnsPerRow = 3,
    imageWidth = "64px",
    ratio = "1/1",
    imageClassName = "",
    textClassName = "",
    animateClass = "",
    // Template props for list items
    itemDisplayVi = null,
    useCompactRenderer = true,
    useFullTemplateMode = true,  // NEW: Full template takeover
    // NEW: Template props for detail views
    detailDisplayVi = null,
    useDetailCompactRenderer = false,
    // NEW: DetailResolver props
    defaultDetailTemplate = null,
    forceDefaultDetail = false,
    // NEW: Search configuration props
    di = null,
    showSearch = true,
    searchPlaceholder = "Search...",
    searchFields = [],
    facetFields = [],
    fuzzySearch = false,
    // NEW: Universal view mode
    useUniversalView = false,
    // Delete button visibility
    showDeleteButton = false,
    style = {},
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    // Validate detailIds
    const safeDetailIds = Array.isArray(detailIds) ? detailIds : [];

    const containerStyle = {
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        ...style,
        ...getSelectionStyle(isSelected, isHovered, enabled)
    };

    if (debug) {
        console.log(`[StackCraft ${name}] Universal Engine Debug:`, {
            detailIds: safeDetailIds,
            itemDisplayVi,
            detailDisplayVi,
            defaultDetailTemplate,
            forceDefaultDetail,
            useCompactRenderer,
            useDetailCompactRenderer,
            useUniversalView,
            viewMode,
            di,
            showSearch,
            searchFields,
            facetFields
        });
    }

    return (
        <div
            ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
            name={name}
            style={containerStyle}
            {...props}
        >
            {useUniversalView ? (
                // NEW: Universal Display Engine with ViewSwitcher
                <ViewSwitcher
                    rootId={rootId}
                    di={di}
                    defaultView={viewMode}
                    showSearch={showSearch}
                    showViewSelector={showViewSelector}
                    showFuzzyToggle={showFuzzyToggle}
                    searchPlaceholder={searchPlaceholder}
                    searchFields={searchFields}
                    facetFields={facetFields}
                    itemTemplate={itemDisplayVi}
                    categoryTemplate={null} // Could be made configurable
                    useCompact={useCompactRenderer}
                    debug={debug}
                    // Delete button visibility
                    showDeleteButton={showDeleteButton}
                    // Tree/Grid view props for when those modes are selected
                    rows={rows}
                    columnsPerRow={columnsPerRow}
                    imageWidth={imageWidth}
                    ratio={ratio}
                    imageClassName={imageClassName}
                    textClassName={textClassName}
                    animateClass={animateClass}
                />
            ) : (
                // Legacy StackContainer mode for backwards compatibility
                <StackContainer
                    rootId={rootId}
                    debug={debug}
                    rows={rows}
                    showDetailView={clickable}
                    detailIds={safeDetailIds}
                    showViewSelector={showViewSelector}
                    showCapture={showCapture}
                    sideBySide={sideBySide}
                    listWidth={listWidth}
                    // Grid props
                    viewMode={viewMode}
                    columnsPerRow={columnsPerRow}
                    imageWidth={imageWidth}
                    ratio={ratio}
                    imageClassName={imageClassName}
                    textClassName={textClassName}
                    animateClass={animateClass}
                    // Template props for list items
                    itemDisplayVi={itemDisplayVi}
                    useCompactRenderer={useCompactRenderer}
                    useFullTemplateMode={useFullTemplateMode}
                    // NEW: Template props for detail views
                    detailDisplayVi={detailDisplayVi}
                    useDetailCompactRenderer={useDetailCompactRenderer}
                    // NEW: DetailResolver props
                    defaultDetailTemplate={defaultDetailTemplate}
                    forceDefaultDetail={forceDefaultDetail}
                />
            )}
        </div>
    );
};

const StackCraftSettings = () => {
    const {
        actions: { setProp },
        name,
        rootId,
        debug,
        rows,
        clickable,
        showCapture,
        sideBySide,
        listWidth,
        width,
        height,
        detailIds,
        showViewSelector,
        showFuzzyToggle,
        // Grid settings
        viewMode,
        columnsPerRow,
        imageWidth,
        ratio,
        imageClassName,
        textClassName,
        animateClass,
        // Template settings for list items
        itemDisplayVi,
        useCompactRenderer,
        useFullTemplateMode,  // NEW
        // NEW: Template settings for detail views
        detailDisplayVi,
        useDetailCompactRenderer,
        // NEW: DetailResolver settings
        defaultDetailTemplate,
        forceDefaultDetail,
        // NEW: Search configuration settings
        di,
        showSearch,
        searchPlaceholder,
        searchFields,
        facetFields,
        fuzzySearch,
        // NEW: Universal view mode
        useUniversalView,
        // Delete button visibility
        showDeleteButton
    } = useNode((node) => ({
        name: node.data.props.name,
        rootId: node.data.props.rootId,
        debug: node.data.props.debug,
        rows: node.data.props.rows,
        clickable: node.data.props.clickable,
        showCapture: node.data.props.showCapture,
        sideBySide: node.data.props.sideBySide,
        listWidth: node.data.props.listWidth,
        width: node.data.props.width,
        height: node.data.props.height,
        detailIds: node.data.props.detailIds || [],
        showViewSelector: node.data.props.showViewSelector,
        showFuzzyToggle: node.data.props.showFuzzyToggle,
        // Grid settings
        viewMode: node.data.props.viewMode,
        columnsPerRow: node.data.props.columnsPerRow,
        imageWidth: node.data.props.imageWidth,
        ratio: node.data.props.ratio,
        imageClassName: node.data.props.imageClassName,
        textClassName: node.data.props.textClassName,
        animateClass: node.data.props.animateClass,
        // Template settings for list items
        itemDisplayVi: node.data.props.itemDisplayVi,
        useCompactRenderer: node.data.props.useCompactRenderer,
        useFullTemplateMode: node.data.props.useFullTemplateMode,  // NEW
        // NEW: Template settings for detail views
        detailDisplayVi: node.data.props.detailDisplayVi,
        useDetailCompactRenderer: node.data.props.useDetailCompactRenderer,
        // NEW: DetailResolver settings
        defaultDetailTemplate: node.data.props.defaultDetailTemplate,
        forceDefaultDetail: node.data.props.forceDefaultDetail,
        // NEW: Search configuration settings
        di: node.data.props.di,
        showSearch: node.data.props.showSearch,
        searchPlaceholder: node.data.props.searchPlaceholder,
        searchFields: node.data.props.searchFields || [],
        facetFields: node.data.props.facetFields || [],
        fuzzySearch: node.data.props.fuzzySearch,
        // NEW: Universal view mode
        useUniversalView: node.data.props.useUniversalView,
        // Delete button visibility
        showDeleteButton: node.data.props.showDeleteButton
    }));

    const [localName, setLocalName] = useState(name);
    const [localRootId, setLocalRootId] = useState(rootId);
    const [localWidth, setLocalWidth] = useState(width);
    const [localHeight, setLocalHeight] = useState(height);
    const [localListWidth, setLocalListWidth] = useState(listWidth);
    const [localItemDisplayVi, setLocalItemDisplayVi] = useState(itemDisplayVi);
    const [localDetailDisplayVi, setLocalDetailDisplayVi] = useState(detailDisplayVi);
    const [useSelfRoot, setUseSelfRoot] = useState(rootId === "self");
    
    // NEW: Local state for universal engine props
    const [localDefaultDetailTemplate, setLocalDefaultDetailTemplate] = useState(defaultDetailTemplate);
    const [localDi, setLocalDi] = useState(di);
    const [localSearchPlaceholder, setLocalSearchPlaceholder] = useState(searchPlaceholder);
    const [localSearchFields, setLocalSearchFields] = useState(searchFields ? searchFields.join(', ') : '');
    const [localFacetFields, setLocalFacetFields] = useState(facetFields ? facetFields.join(', ') : '');

    // For adding new views
    const [newViewName, setNewViewName] = useState('');
    const [newViewId, setNewViewId] = useState('');

    // For editing existing views
    const [editingIndex, setEditingIndex] = useState(null);
    const [editName, setEditName] = useState('');
    const [editId, setEditId] = useState('');

    useEffect(() => setLocalName(name), [name]);
    useEffect(() => setLocalRootId(rootId || ''), [rootId]);
    useEffect(() => setLocalWidth(width), [width]);
    useEffect(() => setLocalHeight(height), [height]);
    useEffect(() => setLocalListWidth(listWidth), [listWidth]);
    useEffect(() => setLocalItemDisplayVi(itemDisplayVi || ''), [itemDisplayVi]);
    useEffect(() => setLocalDetailDisplayVi(detailDisplayVi || ''), [detailDisplayVi]);
    useEffect(() => setUseSelfRoot(rootId === "self"), [rootId]);
    
    // NEW: useEffect hooks for universal engine props
    useEffect(() => setLocalDefaultDetailTemplate(defaultDetailTemplate || ''), [defaultDetailTemplate]);
    useEffect(() => setLocalDi(di || ''), [di]);
    useEffect(() => setLocalSearchPlaceholder(searchPlaceholder || 'Search...'), [searchPlaceholder]);
    useEffect(() => setLocalSearchFields(searchFields ? searchFields.join(', ') : ''), [searchFields]);
    useEffect(() => setLocalFacetFields(facetFields ? facetFields.join(', ') : ''), [facetFields]);

    // Helper to create initial view if none exist
    const createDefaultView = () => {
        const defaultView = { 
            name: "Default View", 
            id: `default_${Date.now()}`
        };
        setProp(props => props.detailIds = [defaultView]);
    };

    const handleAddView = () => {
        if (newViewName.trim() && newViewId.trim()) {
            const exists = detailIds.some(view => view.id === newViewId.trim());
            if (!exists) {
                setProp(props => props.detailIds = [
                    ...detailIds,
                    { name: newViewName.trim(), id: newViewId.trim() }
                ]);
                setNewViewName('');
                setNewViewId('');
            } else {
                alert('A view with this ID already exists in this Stack!');
            }
        }
    };

    const handleRemoveView = (index) => {
        if (detailIds.length > 1) {
            setProp(props => props.detailIds = detailIds.filter((_, i) => i !== index));
        } else {
            alert('Cannot remove the last view! Add another view first.');
        }
    };

    const handleStartEdit = (index) => {
        setEditingIndex(index);
        setEditName(detailIds[index].name);
        setEditId(detailIds[index].id);
    };

    const handleSaveEdit = () => {
        if (editName.trim() && editId.trim()) {
            const exists = detailIds.some((view, i) => i !== editingIndex && view.id === editId.trim());
            if (!exists) {
                setProp(props => {
                    const newDetailIds = [...detailIds];
                    newDetailIds[editingIndex] = { name: editName.trim(), id: editId.trim() };
                    props.detailIds = newDetailIds;
                });
                setEditingIndex(null);
                setEditName('');
                setEditId('');
            } else {
                alert('A view with this ID already exists in this Stack!');
            }
        }
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setEditName('');
        setEditId('');
    };

    // Validation for detailIds
    const validateDetailIds = () => {
        if (!Array.isArray(detailIds) || detailIds.length === 0) {
            return null;
        }

        const ids = detailIds.map(view => view.id);
        const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
        
        if (duplicates.length > 0) {
            return `Duplicate IDs in this Stack: ${duplicates.join(', ')}`;
        }
        
        const invalidViews = detailIds.filter(view => !view.name || !view.id);
        if (invalidViews.length > 0) {
            return 'Some views have missing name or ID';
        }
        
        return null;
    };

    const validationError = validateDetailIds();

    return (
        <div>
            <FormControl fullWidth margin="normal">
                <FormLabel>Component Name</FormLabel>
                <TextField
                    value={localName || ''}
                    onChange={(e) => setLocalName(e.target.value)}
                    onBlur={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Root ID (leave empty to use current user)</FormLabel>
                <TextField
                    value={localRootId || ''}
                    onChange={(e) => setLocalRootId(e.target.value)}
                    onBlur={(e) => setProp(props => props.rootId = e.target.value || null)}
                    size="small"
                    placeholder="Leave empty for current user"
                    disabled={useSelfRoot}
                />
            </FormControl>

            {/* Universal Engine Mode Toggle */}
            <FormControl fullWidth margin="normal">
                <FormControlLabel
                    control={
                        <Switch
                            checked={useUniversalView}
                            onChange={(e) => setProp(props => props.useUniversalView = e.target.checked)}
                        />
                    }
                    label="Use Universal Display Engine"
                    style={{ marginBottom: '8px' }}
                />
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                    Enable to access advanced search, flat/hybrid views, and all universal engine features.
                    When disabled, uses legacy StackContainer for backwards compatibility.
                </div>
            </FormControl>

            {/* View Mode Section */}
            <FormControl fullWidth margin="normal">
                <FormLabel>View Mode</FormLabel>
                <Select
                    value={viewMode || "tree"}
                    onChange={(e) => setProp(props => props.viewMode = e.target.value)}
                    size="small"
                >
                    <MenuItem value="tree">
                        <ViewListIcon style={{ marginRight: '8px' }} />
                        Tree View
                    </MenuItem>
                    <MenuItem value="flat">
                        <ViewListIcon style={{ marginRight: '8px' }} />
                        Flat View {useUniversalView ? '(with search)' : '(requires Universal)'}
                    </MenuItem>
                    <MenuItem value="hybrid">
                        <ViewListIcon style={{ marginRight: '8px' }} />
                        Hybrid View {useUniversalView ? '(categories + items)' : '(requires Universal)'}
                    </MenuItem>
                    <MenuItem value="grid">
                        <GridViewIcon style={{ marginRight: '8px' }} />
                        Grid View
                    </MenuItem>
                </Select>
            </FormControl>

            {/* Universal Engine Configuration */}
            {useUniversalView && (
                <Box style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    padding: '12px', 
                    marginTop: '8px',
                    backgroundColor: '#fff5f5'
                }}>
                    <FormLabel style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                        Universal Engine Configuration
                    </FormLabel>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Domain ID (di) - Optional Security Scope</FormLabel>
                        <TextField
                            value={localDi || ''}
                            onChange={(e) => setLocalDi(e.target.value)}
                            onBlur={(e) => setProp(props => props.di = e.target.value || null)}
                            size="small"
                            placeholder="Leave empty for no domain filtering"
                            helperText="UUID to restrict results to specific domain/user"
                        />
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={showSearch}
                                onChange={(e) => setProp(props => props.showSearch = e.target.checked)}
                            />
                        }
                        label="Show Search Interface"
                        style={{ marginTop: '8px' }}
                    />

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Search Placeholder Text</FormLabel>
                        <TextField
                            value={localSearchPlaceholder || ''}
                            onChange={(e) => setLocalSearchPlaceholder(e.target.value)}
                            onBlur={(e) => setProp(props => props.searchPlaceholder = e.target.value || 'Search...')}
                            size="small"
                            placeholder="Search..."
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Search Fields (comma-separated)</FormLabel>
                        <TextField
                            value={localSearchFields || ''}
                            onChange={(e) => setLocalSearchFields(e.target.value)}
                            onBlur={(e) => {
                                const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                setProp(props => props.searchFields = fields);
                            }}
                            size="small"
                            placeholder="title, description, content, name"
                            helperText="Fields to search in. Leave empty to search all text fields."
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Facet Fields (comma-separated)</FormLabel>
                        <TextField
                            value={localFacetFields || ''}
                            onChange={(e) => setLocalFacetFields(e.target.value)}
                            onBlur={(e) => {
                                const fields = e.target.value.split(',').map(f => f.trim()).filter(f => f);
                                setProp(props => props.facetFields = fields);
                            }}
                            size="small"
                            placeholder="qwantaType, status, priority"
                            helperText="Fields to generate filter facets for"
                        />
                    </FormControl>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={fuzzySearch}
                                onChange={(e) => setProp(props => props.fuzzySearch = e.target.checked)}
                            />
                        }
                        label="Enable Fuzzy Search by Default"
                        style={{ marginTop: '8px' }}
                    />
                </Box>
            )}

            {/* Template Section for List Items */}
            <Box style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '12px', 
                marginTop: '8px',
                backgroundColor: '#f0f8ff'
            }}>
                <FormLabel style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    List Item Template Settings
                </FormLabel>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Item Display Template (VI)</FormLabel>
                    <TextField
                        value={localItemDisplayVi || ''}
                        onChange={(e) => setLocalItemDisplayVi(e.target.value)}
                        onBlur={(e) => setProp(props => props.itemDisplayVi = e.target.value || null)}
                        size="small"
                        placeholder="Enter card UUID for custom item display (optional)"
                        helperText="Leave empty for default text display"
                    />
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={useCompactRenderer}
                            onChange={(e) => setProp(props => props.useCompactRenderer = e.target.checked)}
                        />
                    }
                    label="Use Compact Renderer for List Templates"
                    style={{ marginTop: '8px' }}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={useFullTemplateMode}
                            onChange={(e) => setProp(props => props.useFullTemplateMode = e.target.checked)}
                        />
                    }
                    label="Full Template Mode (No Icon/Margins)"
                    style={{ marginTop: '8px' }}
                />
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    When enabled, template takes complete control with no icon or StackItem formatting.
                </div>
            </Box>

            {/* NEW: Template Section for Detail Views */}
            <Box style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '12px', 
                marginTop: '8px',
                backgroundColor: '#f0fff0'
            }}>
                <FormLabel style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    Detail View Template Settings
                </FormLabel>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Detail View Template (VI)</FormLabel>
                    <TextField
                        value={localDetailDisplayVi || ''}
                        onChange={(e) => setLocalDetailDisplayVi(e.target.value)}
                        onBlur={(e) => setProp(props => props.detailDisplayVi = e.target.value || null)}
                        size="small"
                        placeholder="Enter card UUID for universal detail view template (optional)"
                        helperText="When set, ALL detail views use this template instead of individual vi settings"
                    />
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={useDetailCompactRenderer}
                            onChange={(e) => setProp(props => props.useDetailCompactRenderer = e.target.checked)}
                        />
                    }
                    label="Use Compact Renderer for Detail Templates"
                    style={{ marginTop: '8px' }}
                />
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Detail views typically use full rendering. Enable compact only if template is designed for it.
                </div>
            </Box>

            {/* NEW: DetailResolver Settings */}
            <Box style={{ 
                border: '1px solid #ddd', 
                borderRadius: '4px', 
                padding: '12px', 
                marginTop: '8px',
                backgroundColor: '#f0fff0'
            }}>
                <FormLabel style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                    Advanced Detail Resolution Settings
                </FormLabel>

                <FormControl fullWidth margin="normal">
                    <FormLabel>Default Detail Template (Universal Fallback)</FormLabel>
                    <TextField
                        value={localDefaultDetailTemplate || ''}
                        onChange={(e) => setLocalDefaultDetailTemplate(e.target.value)}
                        onBlur={(e) => setProp(props => props.defaultDetailTemplate = e.target.value || null)}
                        size="small"
                        placeholder="Enter card UUID for default detail template"
                        helperText="Used when items don't have their own vi or card data"
                    />
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={forceDefaultDetail}
                            onChange={(e) => setProp(props => props.forceDefaultDetail = e.target.checked)}
                        />
                    }
                    label="Force Default Detail Template"
                    style={{ marginTop: '8px' }}
                />
                <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    When enabled, ALL items use the default template, ignoring individual vi/card settings.
                    Useful for consistent detail view across all items.
                </div>

                <div style={{ 
                    backgroundColor: '#e8f4fd', 
                    border: '1px solid #bee5eb', 
                    borderRadius: '4px', 
                    padding: '8px', 
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#055160'
                }}>
                    <strong>Detail Resolution Priority:</strong><br/>
                    1. Custom DetailComponent (if provided)<br/>
                    2. Force default template (if enabled)<br/>
                    3. Item's specific template (item.vi)<br/>
                    4. Item's embedded card data (item.card)<br/>
                    5. Default template (if configured)<br/>
                    6. Fallback message
                </div>
            </Box>

            {/* Grid-specific settings */}
            {viewMode === "grid" && (
                <Box style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    padding: '12px', 
                    marginTop: '8px',
                    backgroundColor: '#f9f9f9'
                }}>
                    <FormLabel style={{ fontWeight: 'bold', marginBottom: '8px', display: 'block' }}>
                        Grid Settings
                    </FormLabel>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Columns Per Row</FormLabel>
                        <TextField
                            type="number"
                            value={columnsPerRow || 3}
                            onChange={(e) => setProp(props => props.columnsPerRow = parseInt(e.target.value) || 3)}
                            size="small"
                            inputProps={{ min: 1, max: 8 }}
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Image Width</FormLabel>
                        <TextField
                            value={imageWidth || "64px"}
                            onChange={(e) => setProp(props => props.imageWidth = e.target.value)}
                            size="small"
                            placeholder="64px, 80px, 100px"
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Image Ratio</FormLabel>
                        <Select
                            value={ratio || "1/1"}
                            onChange={(e) => setProp(props => props.ratio = e.target.value)}
                            size="small"
                        >
                            <MenuItem value="1/1">Square (1:1)</MenuItem>
                            <MenuItem value="4/3">Standard (4:3)</MenuItem>
                            <MenuItem value="16/9">Widescreen (16:9)</MenuItem>
                            <MenuItem value="3/4">Portrait (3:4)</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Image CSS Classes</FormLabel>
                        <TextField
                            value={imageClassName || ""}
                            onChange={(e) => setProp(props => props.imageClassName = e.target.value)}
                            size="small"
                            placeholder="rounded-lg shadow-md border"
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Text CSS Classes</FormLabel>
                        <TextField
                            value={textClassName || ""}
                            onChange={(e) => setProp(props => props.textClassName = e.target.value)}
                            size="small"
                            placeholder="text-sm font-medium"
                        />
                    </FormControl>

                    <FormControl fullWidth margin="normal">
                        <FormLabel>Animation Classes (Animate.css)</FormLabel>
                        <TextField
                            value={animateClass || ""}
                            onChange={(e) => setProp(props => props.animateClass = e.target.value)}
                            size="small"
                            placeholder="fadeIn, bounceIn, slideInUp"
                        />
                    </FormControl>
                </Box>
            )}

            <FormControlLabel
                control={
                    <Switch
                        checked={showViewSelector}
                        onChange={(e) => setProp(props => props.showViewSelector = e.target.checked)}
                    />
                }
                label="Show View Selector Dropdown"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={showFuzzyToggle}
                        onChange={(e) => setProp(props => props.showFuzzyToggle = e.target.checked)}
                    />
                }
                label="Show Fuzzy Search Toggle"
            />

            <FormControl fullWidth margin="normal">
                <FormLabel>Detail Views for This Stack</FormLabel>
                
                {/* Show helpful info when no views configured */}
                {detailIds.length === 0 && (
                    <Box style={{ 
                        backgroundColor: '#e8f4fd', 
                        border: '1px solid #bee5eb', 
                        borderRadius: '4px', 
                        padding: '12px', 
                        marginBottom: '8px' 
                    }}>
                        <div style={{ color: '#055160', fontSize: '14px', marginBottom: '8px' }}>
                            ℹ️ No detail views configured for this Stack instance.
                        </div>
                        <Button 
                            size="small" 
                            variant="contained" 
                            onClick={createDefaultView}
                            style={{ fontSize: '12px' }}
                        >
                            Create First View
                        </Button>
                    </Box>
                )}
                
                {/* Stack-specific validation warning */}
                {validationError && (
                    <Box style={{ 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7', 
                        borderRadius: '4px', 
                        padding: '8px', 
                        marginBottom: '8px' 
                    }}>
                        <span style={{ color: '#856404', fontSize: '14px' }}>
                            ⚠️ {validationError}
                        </span>
                    </Box>
                )}
                
                <Box style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px' }}>
                    {/* Existing views list */}
                    {detailIds.map((view, index) => (
                        <Box key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginBottom: '8px',
                            padding: '8px',
                            backgroundColor: '#f9f9f9',
                            borderRadius: '4px'
                        }}>
                            {editingIndex === index ? (
                                <>
                                    <TextField
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        size="small"
                                        placeholder="View name"
                                        style={{ flex: '1' }}
                                    />
                                    <TextField
                                        value={editId}
                                        onChange={(e) => setEditId(e.target.value)}
                                        size="small"
                                        placeholder="View ID"
                                        style={{ flex: '1' }}
                                    />
                                    <IconButton onClick={handleSaveEdit} size="small" color="primary">
                                        <CheckIcon />
                                    </IconButton>
                                    <IconButton onClick={handleCancelEdit} size="small">
                                        <CloseIcon />
                                    </IconButton>
                                </>
                            ) : (
                                <>
                                    <Box style={{ flex: '1' }}>
                                        <div style={{ fontWeight: 'bold' }}>{view.name}</div>
                                        <div style={{ fontSize: '12px', color: '#666' }}>{view.id}</div>
                                    </Box>
                                    <IconButton onClick={() => handleStartEdit(index)} size="small">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => handleRemoveView(index)}
                                        size="small"
                                        disabled={detailIds.length <= 1}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </>
                            )}
                        </Box>
                    ))}

                    {/* Add new view */}
                    <Box style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: detailIds.length > 0 ? '1px solid #eee' : 'none'
                    }}>
                        <TextField
                            value={newViewName}
                            onChange={(e) => setNewViewName(e.target.value)}
                            size="small"
                            placeholder="View name"
                            style={{ flex: '1' }}
                        />
                        <TextField
                            value={newViewId}
                            onChange={(e) => setNewViewId(e.target.value)}
                            size="small"
                            placeholder="View ID (like card _id)"
                            style={{ flex: '1' }}
                        />
                        <IconButton
                            onClick={handleAddView}
                            disabled={!newViewName.trim() || !newViewId.trim() ||
                                detailIds.some(view => view?.id === newViewId.trim())}
                            size="small"
                            color="primary"
                        >
                            <AddIcon />
                        </IconButton>
                    </Box>
                </Box>
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Container Width</FormLabel>
                <TextField
                    value={localWidth}
                    onChange={(e) => setLocalWidth(e.target.value)}
                    onBlur={(e) => setProp(props => props.width = e.target.value)}
                    size="small"
                    placeholder="350px for replace, 700px+ for side-by-side"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Height</FormLabel>
                <TextField
                    value={localHeight}
                    onChange={(e) => setLocalHeight(e.target.value)}
                    onBlur={(e) => setProp(props => props.height = e.target.value)}
                    size="small"
                    placeholder="400px, 100vh, auto"
                />
            </FormControl>

            <FormControl fullWidth margin="normal">
                <FormLabel>Max Rows Per Level</FormLabel>
                <TextField
                    type="number"
                    value={rows}
                    onChange={(e) => setProp(props => props.rows = parseInt(e.target.value) || 100)}
                    size="small"
                    inputProps={{ min: 10, max: 1000 }}
                />
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={clickable}
                        onChange={(e) => setProp(props => props.clickable = e.target.checked)}
                    />
                }
                label="Clickable Items (Enable Detail View)"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={sideBySide}
                        onChange={(e) => setProp(props => props.sideBySide = e.target.checked)}
                    />
                }
                label="Side-by-Side Mode"
            />

            <FormControl fullWidth margin="normal">
                <FormLabel>List Width (for side-by-side mode)</FormLabel>
                <TextField
                    value={localListWidth}
                    onChange={(e) => setLocalListWidth(e.target.value)}
                    onBlur={(e) => setProp(props => props.listWidth = e.target.value)}
                    size="small"
                    placeholder="350px, 50%, 450px"
                />
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={showCapture}
                        onChange={(e) => setProp(props => props.showCapture = e.target.checked)}
                    />
                }
                label="Show Capture Component"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={useSelfRoot}
                        onChange={(e) => {
                            const newValue = e.target.checked;
                            setUseSelfRoot(newValue);
                            setProp(props => props.rootId = newValue ? "self" : null);
                            if (newValue) {
                                setLocalRootId("self");
                            }
                        }}
                    />
                }
                label="Use Self as Root"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={showDeleteButton || false}
                        onChange={(e) => setProp(props => props.showDeleteButton = e.target.checked)}
                    />
                }
                label="Show Delete Button (Cross)"
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={debug}
                        onChange={(e) => setProp(props => props.debug = e.target.checked)}
                    />
                }
                label="Debug Mode"
            />
        </div>
    );
};

// Craft configuration with detail template settings
StackCraft.craft = {
    props: {
        name: "StackCraft",
        rootId: null,
        debug: false,
        rows: 100,
        clickable: true,
        showCapture: true,
        sideBySide: false,
        listWidth: "350px",
        width: "350px",
        height: "400px",
        detailIds: [],
        showViewSelector: true,
        showFuzzyToggle: true,
        // Grid default props
        viewMode: "tree",
        columnsPerRow: 3,
        imageWidth: "64px",
        ratio: "1/1",
        imageClassName: "",
        textClassName: "",
        animateClass: "",
        // Template default props for list items
        itemDisplayVi: null,
        useCompactRenderer: true,
        useFullTemplateMode: true,  // NEW: Default to full template mode
        // NEW: Template default props for detail views
        detailDisplayVi: null,
        useDetailCompactRenderer: false,
        // NEW: DetailResolver default props
        defaultDetailTemplate: null,
        forceDefaultDetail: false,
        // NEW: Search configuration default props
        di: null,
        showSearch: true,
        searchPlaceholder: "Search...",
        searchFields: [],
        facetFields: [],
        fuzzySearch: false,
        // NEW: Universal view mode default
        useUniversalView: false,
        // Delete button visibility
        showDeleteButton: false
    },
    related: {
        settings: StackCraftSettings
    },
    displayName: "StackCraft2"
};